import { useState } from 'react';

import { SplitMethodPicker } from './SplitMethodPicker';
import type { ClaimOutcome } from '@/hooks/useSessionBills';
import type {
  ClaimShareRequest,
  SessionBillItem,
  SessionBillLimits,
} from '@/lib/api/split-payment';
import {
  MAX_DENOMINATOR,
  toClaimRequest,
  type PartialAmountVerdict,
  type SplitMethod,
} from '@/lib/splitMethods';
import type { Currency } from '@/types';
import { formatCurrency } from '@/utils';

interface Props {
  items: readonly SessionBillItem[];
  limits: SessionBillLimits;
  comensalCount: number;
  myBillId: number | null;
  canClaim: boolean;
  isClaiming: boolean;
  currency: Currency;
  onClaim: (request: ClaimShareRequest) => Promise<ClaimOutcome>;
  /** iguales는 서버 원자 연산만 허용한다. */
  onClaimEqual?: (denominator: number) => Promise<ClaimOutcome>;
  labels: SplitClaimPanelLabels;
}

export interface SplitClaimPanelLabels {
  methodTitle: string;
  methods: Record<SplitMethod, string>;
  amountPlaceholder: string;
  confirm: string;
  amountErrors: Record<Exclude<PartialAmountVerdict, 'ok'>, string>;
  itemsTitle: string;
  fractionLabel: string;
  wholeItem: string;
  equalTitle: string;
  equalConfirm: string;
  conflict: string;
  failed: string;
  unavailable: string;
  myShare: string;
}

export function SplitClaimPanel({
  items,
  limits,
  comensalCount,
  myBillId,
  canClaim,
  isClaiming,
  currency,
  onClaim,
  onClaimEqual,
  labels,
}: Props) {
  const [method, setMethod] = useState<SplitMethod | null>(null);
  const [denominator, setDenominator] = useState(2);
  const [message, setMessage] = useState<string | null>(null);

  if (!canClaim) return null;

  const handleOutcome = (outcome: ClaimOutcome): void => {
    setMessage(
      outcome === 'conflict' ? labels.conflict : outcome === 'error' ? labels.failed : null,
    );
  };

  const claimRequest = (request: ClaimShareRequest | null): void => {
    if (request === null || isClaiming) return;
    setMessage(null);
    void onClaim(request).then(handleOutcome);
  };

  const claimItem = (itemId: number): void => {
    if (method !== 'consumi' && method !== 'articulos') return;
    claimRequest(toClaimRequest(method, {
      orderItemId: itemId,
      denominator: method === 'articulos' ? denominator : 1,
    }));
  };

  const claimEqual = (): void => {
    if (
      onClaimEqual === undefined
      || isClaiming
      || comensalCount < 2
      || comensalCount > MAX_DENOMINATOR
    ) return;
    setMessage(null);
    void onClaimEqual(comensalCount).then(handleOutcome);
  };

  return (
    <div className="space-y-3" data-testid="split-claim-panel">
      <SplitMethodPicker
        isSplitAvailable
        comensalCount={comensalCount}
        limits={{
          minimumMinor: limits.minimum_partial_transfer_minor,
          outstandingMinor: limits.residual_available_minor,
        }}
        currency={currency}
        onSelect={setMethod}
        onSubmitAmount={(amountMinor) => {
          claimRequest(toClaimRequest('monto', { amountMinor }));
        }}
        labels={{
          title: labels.methodTitle,
          methods: labels.methods,
          amountPlaceholder: labels.amountPlaceholder,
          confirm: labels.confirm,
          errors: labels.amountErrors,
        }}
      />

      {method === 'consumi' || method === 'articulos' ? (
        <section className="rounded-lg border border-border bg-card p-4">
          <h4 className="text-sm font-semibold text-foreground">{labels.itemsTitle}</h4>

          {method === 'articulos' ? (
            <label className="mt-3 block text-xs font-medium text-foreground">
              {labels.fractionLabel}
              <select
                value={denominator}
                onChange={(event) => { setDenominator(Number(event.target.value)); }}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="split-claim-denominator"
              >
                <option value={1}>{labels.wholeItem}</option>
                {Array.from({ length: MAX_DENOMINATOR - 1 }, (_, index) => index + 2).map((value) => (
                  <option key={value} value={value}>1/{value}</option>
                ))}
              </select>
            </label>
          ) : null}

          <ul className="mt-3 space-y-2">
            {items.map((item) => {
              const unavailable = item.fully_claimed || item.residual_amount <= 0;
              return (
              <li key={item.order_item_id} className="space-y-1">
                <button
                  type="button"
                  onClick={() => { claimItem(item.order_item_id); }}
                  disabled={isClaiming || unavailable}
                  className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm disabled:opacity-50"
                  data-testid={`split-claim-item-${item.order_item_id}`}
                >
                  <span>{item.name}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {formatCurrency(item.residual_amount, currency)}
                  </span>
                </button>
                {item.claims.length > 0 ? (
                  <ul className="space-y-1 pl-2" aria-label={`${item.name} claims`}>
                    {item.claims.map((claim) => {
                      const percentage = claim.share_ppm / 10000;
                      return (
                        <li
                          key={`${claim.bill_id}:${claim.share_ppm}:${claim.amount}`}
                          className="flex items-center justify-between text-xs text-muted-foreground"
                        >
                          <span>
                            {claim.comensal_name ?? `#${String(claim.bill_id)}`}
                            {claim.bill_id === myBillId ? ` · ${labels.myShare}` : ''}
                          </span>
                          <span className="tabular-nums">
                            {formatCurrency(claim.amount, currency)} · {percentage.toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}%
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {method === 'iguales' ? (
        <section className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs font-medium text-foreground">
            {labels.equalTitle}: <strong data-testid="split-equal-denominator">{comensalCount}</strong>
          </p>
          <button
            type="button"
            onClick={claimEqual}
            disabled={
              isClaiming
              || onClaimEqual === undefined
              || comensalCount < 2
              || comensalCount > MAX_DENOMINATOR
            }
            className="mt-3 w-full rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
            data-testid="split-equal-confirm"
          >
            {labels.equalConfirm}
          </button>
        </section>
      ) : null}

      {message !== null ? (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {message}
        </p>
      ) : null}
    </div>
  );
}
